import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useGetUserPermissionsQuery, UserPermissions } from './SettingsApi';

type CategoryName = 'workflow' | 'application' | 'capture' | 'agents';

// Main category -> the boolean the /api/settings/user/permissions endpoint returns for it.
const CATEGORY_FLAGS: Record<CategoryName, keyof UserPermissions> = {
  workflow: 'hasWorkflowCategory',
  application: 'hasApplicationCategory',
  capture: 'hasCaptureCategory',
  agents: 'hasAgentsCategory',
};

/**
 * Route guard for pages gated by a main permission category - the group-level
 * toggles in Settings > Groups (WORKFLOW, APPLICATION, CAPTURE, AGENTS, ...).
 * Super admins bypass group permissions; tenant feature switches still apply.
 * Everyone else needs the category enabled on one of
 * their groups. Mirrors the nav filtering in NavigationLoader so a hidden nav
 * item cannot be reached by typing its URL.
 */
export default function RequireCategory({
  category,
  children,
}: {
  category: CategoryName;
  children: React.ReactNode;
}) {
  const { data, isLoading } = useGetUserPermissionsQuery();
  const location = useLocation();

  if (isLoading) {
    return <div />;
  }

  const isSuperAdmin = data?.user?.isSuperAdmin === true;
  const hasCategory = data?.subcategoryPermissions?.[CATEGORY_FLAGS[category]] === true;

  const tenantDisabled = category === 'agents' && data?.tenantFeatures?.agentsEnabled !== true;

  if (tenantDisabled || (!isSuperAdmin && !hasCategory)) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return children;
}
